import { Breadcrumb, Button, Checkbox, Collapse, Divider, Input, Layout, Modal, Popconfirm, Result, Skeleton, notification, theme } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { EyeOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiGetEventCheckoutDetails, apiMarkCheckoutAsPaid, apiResendCheckoutEmail } from '../../../../api/events';
import { EMPTY_VALUE, formatDateTime, formatPhoneWithCountry, formatPrice } from '../../../../utils/format';

const EventCheckoutDetails = () => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const navigate = useNavigate();
  const { accountId, eventId, checkoutId } = useParams();
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState("");
  const [checkout, setCheckout] = useState(null);
  const [markingAsPaid, setMarkingAsPaid] = useState(false);
  const [sendEmailOnPay, setSendEmailOnPay] = useState(true);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);

  const statusTranslations = {
    FREE: "Gratuito",
    EXPIRED: "Expirado",
    PENDING: "Pendente",
    PAID: "Pago",
    CANCELED: "Cancelado"
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErrorStatus("");
    try {
      setCheckout(await apiGetEventCheckoutDetails(eventId, checkoutId, accountId));
    } catch (error) {
      setErrorStatus(String(error?.status || "500"));
    } finally {
      setLoading(false);
    }
  }, [accountId, eventId, checkoutId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setResendOpen(false);
    setResendEmail('');
  }, [checkoutId]);

  const canMarkAsPaid = ['PENDING', 'EXPIRED'].includes(checkout?.status);
  const isPaid = ['PAID', 'FREE'].includes(checkout?.status);

  const handleMarkAsPaid = async () => {
    setMarkingAsPaid(true);
    try {
      await apiMarkCheckoutAsPaid(eventId, checkoutId, accountId, sendEmailOnPay);
      notification.success({
        message: 'Compra marcada como paga',
        placement: 'topRight',
      });
      await load();
    } catch (error) {
      const serverMessage = typeof error?.message === 'string'
        ? error.message
        : (error?.message?.error || error?.message?.detail);
      notification.error({
        message: 'Erro',
        description: serverMessage || 'Não foi possível marcar a compra como paga.',
        placement: 'topRight',
      });
    } finally {
      setMarkingAsPaid(false);
    }
  };

  const openResendModal = () => {
    setResendEmail('');
    setResendOpen(true);
  };

  const closeResendModal = () => {
    setResendOpen(false);
    setResendEmail('');
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await apiResendCheckoutEmail(eventId, checkoutId, accountId, resendEmail.trim() || null);
      notification.success({
        message: 'Ingressos reenviados',
        placement: 'topRight',
      });
      closeResendModal();
    } catch (error) {
      const serverMessage = typeof error?.message === 'string'
        ? error.message
        : (error?.message?.error || error?.message?.detail);
      notification.error({
        message: 'Erro',
        description: serverMessage || 'Não foi possível reenviar os ingressos.',
        placement: 'topRight',
      });
    } finally {
      setResending(false);
    }
  };

  if (errorStatus) {
    const subTitle = errorStatus === "404"
      ? "Compra não encontrada para este evento."
      : errorStatus === "403"
        ? "Sem acesso à conta responsável por este evento."
        : "Ocorreu um erro no servidor, tente novamente mais tarde.";
    return (
      <div className="EventDetails">
        <Layout style={{ padding: "1.25rem" }}>
          <Result
            status={errorStatus === "500" ? "500" : "404"}
            title={errorStatus}
            subTitle={subTitle}
            extra={<Button onClick={() => navigate(-1)} type="primary">Voltar</Button>}
          />
        </Layout>
      </div>
    );
  }

  return (
    <div className="EventDetails">
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Breadcrumb
          style={{
            margin: "16px 0",
          }}
        >
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            {loading
              ? <Skeleton active paragraph={{ rows: 1 }} />
              : <h2 className="title-page">{checkout?.customer?.name || EMPTY_VALUE}</h2>}
          </div>
          <Divider />
          {loading
            ? <Skeleton active paragraph={{ rows: 0 }} />
            : (
              <>
                <Breadcrumb.Item>
                  <Link to={`/eventos/${accountId}/${eventId}`}>Evento</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>{checkout?.customer?.name || EMPTY_VALUE}</Breadcrumb.Item>
              </>
            )}
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: "30px 0 0 0",
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div className="container-subtitle">
            {loading
              ? <Skeleton active paragraph={{ rows: 1 }} />
              : <h3 className="subtitle-page">1. Infos do Comprador</h3>}
          </div>
          <Divider />
          {loading
            ? <Skeleton active paragraph={{ rows: 3 }} />
            : (
              <div className="container-inputs">
                <div className="container-left">
                  <div>
                    <p>Nome do comprador</p>
                    <Input disabled value={checkout?.customer?.name || EMPTY_VALUE} />
                  </div>
                  <div>
                    <p>Email do comprador</p>
                    <Input disabled value={checkout?.customer?.email || EMPTY_VALUE} />
                  </div>
                </div>
                <div className="container-right">
                  <div>
                    <p>Telefone</p>
                    <Input disabled value={formatPhoneWithCountry(checkout?.customer?.country_code, checkout?.customer?.phone_number) || EMPTY_VALUE} />
                  </div>
                  <div>
                    <p>Data da compra</p>
                    <Input disabled value={formatDateTime(checkout?.created_at) || EMPTY_VALUE} />
                  </div>
                </div>
              </div>
            )}
        </Content>

        <Content
          style={{
            padding: 24,
            margin: "30px 0 0 0",
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div className="container-subtitle">
            {loading
              ? <Skeleton active paragraph={{ rows: 1 }} />
              : <h3 className="subtitle-page">2. Pagamento</h3>}
          </div>
          <Divider />
          {loading
            ? <Skeleton active paragraph={{ rows: 3 }} />
            : (
              <>
                <div className="container-inputs">
                  <div className="container-left">
                    <div>
                      <p>Status</p>
                      <Input disabled value={statusTranslations[checkout?.status] || EMPTY_VALUE} />
                    </div>
                    <div>
                      <p>Valor final</p>
                      <Input disabled value={formatPrice((checkout?.total ?? 0) / 100)} />
                    </div>
                    <div>
                      <p>Valor original</p>
                      <Input disabled value={formatPrice((checkout?.original_total ?? checkout?.total ?? 0) / 100)} />
                    </div>
                    <div>
                      <p>Desconto</p>
                      <Input disabled value={formatPrice((checkout?.discount_amount ?? 0) / 100)} />
                    </div>
                  </div>
                  <div className="container-right">
                    <div>
                      <p>Cupom</p>
                      <Input disabled value={checkout?.coupon ? `${checkout.coupon.code} (${checkout.coupon.type === 'PERCENTAGE' ? `${checkout.coupon.value}%` : formatPrice(checkout.coupon.value / 100)})` : EMPTY_VALUE} />
                    </div>
                    <div>
                      <p>Pago manualmente em</p>
                      <Input disabled value={formatDateTime(checkout?.paid_manually_at) || EMPTY_VALUE} />
                    </div>
                    <div>
                      <p>Transação</p>
                      {checkout?.transaction_id
                        ? (
                          <div className='input-container'>
                            <Input disabled value={checkout.transaction_id} />
                            <EyeOutlined
                              onClick={() => window.open(`/transacao/${checkout.transaction_id}`, "_blank")}
                              className='eye-icon'
                            />
                          </div>
                        )
                        : <Input disabled value={EMPTY_VALUE} />}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                  {canMarkAsPaid && (
                    <Popconfirm
                      title="Marcar como paga"
                      description="Confirma que esta compra foi paga fora da plataforma?"
                      okText="Confirmar"
                      cancelText="Cancelar"
                      onConfirm={handleMarkAsPaid}
                      onOpenChange={(open) => open && setSendEmailOnPay(true)}
                    >
                      <Button type="primary" loading={markingAsPaid}>Marcar como pago</Button>
                    </Popconfirm>
                  )}
                  {canMarkAsPaid && (
                    <Checkbox checked={sendEmailOnPay} onChange={(e) => setSendEmailOnPay(e.target.checked)}>
                      Enviar email de confirmação
                    </Checkbox>
                  )}
                  {isPaid && <Button onClick={openResendModal}>Reenviar ingressos</Button>}
                </div>
                <Modal
                  title="Reenviar ingressos"
                  open={resendOpen}
                  onOk={handleResend}
                  onCancel={closeResendModal}
                  confirmLoading={resending}
                  okText="Reenviar"
                  cancelText="Cancelar"
                >
                  <p>Deixe em branco para enviar para {checkout?.customer?.email}.</p>
                  <Input
                    placeholder="Outro email (opcional)"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                  />
                </Modal>
              </>
            )}
        </Content>

        <Content
          style={{
            padding: 24,
            margin: "30px 0 0 0",
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div className="container-subtitle">
            {loading
              ? <Skeleton active paragraph={{ rows: 1 }} />
              : <h3 className="subtitle-page">3. Tickets Comprados</h3>}
          </div>
          <Divider />
          {loading
            ? <Skeleton active paragraph={{ rows: 3 }} />
            : (
              <Collapse>
                {checkout?.tickets?.map((ticketCheckout, index) => (
                  <Collapse.Panel
                    header={
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                        <span>{`Ingresso ${index + 1}: ${ticketCheckout.ticket?.name || EMPTY_VALUE}`}</span>
                        <span>
                          {formatPrice((ticketCheckout.ticket?.amount ?? 0) / 100)}
                          {ticketCheckout.used_at ? ` · Utilizado em ${formatDateTime(ticketCheckout.used_at)}` : ' · Não utilizado'}
                        </span>
                      </div>
                    }
                    key={ticketCheckout.id}
                  >
                    {ticketCheckout.answers?.length > 0
                      ? <h4>Respostas:</h4>
                      : <h4 style={{ fontStyle: 'italic', opacity: '0.6' }}>Esse ingresso não possui respostas.</h4>}
                    {ticketCheckout.answers?.map((answer, answerIndex) => (
                      <div key={answerIndex}>
                        <p><strong>{answer.question}: <br /></strong> {answer.answer}</p>
                      </div>
                    ))}
                  </Collapse.Panel>
                ))}
              </Collapse>
            )}
        </Content>
      </Layout>
    </div>
  );
};

export default EventCheckoutDetails;
